// Lógica de la cola de Publicar (P3/P4/D1), sin UI: borradores, validación y publicación por ítem.
import { api } from "@/lib/api";
import { compressImage, humanUploadError } from "@/lib/imageCompress";

export const MIN_PHOTOS = 2;
export const MAX_ITEMS = 20;

export async function uploadPublic(file) {
  const { file: toUpload } = await compressImage(file);
  const fd = new FormData();
  fd.append("file", toUpload);
  fd.append("scope", "public");
  fd.append("kind", "card");
  const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
  return r.data.path;
}

export const newRef = () => window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36);

export const emptyDraft = () => ({
  id: newRef(),
  selected: null,
  photos: [],
  form: {
    language: "en", condition: "NM", price: "", quantity: 1,
    language_other: "", ship_cost: 5, free_ship_min_items: "", free_ship_min_amount: "",
    variant: "normal", bulk_disclaimer_accepted: false, ship_cost_from_config: false,
    yugioh_rarity: "", yugioh_set_code: "",
  },
  reference: null,
});

export const isSealedSel = (sel) => !!(sel?.is_sealed || (sel?.product_type && sel.product_type !== "card"));
export const photoCount = (d) => d.photos.length + (d.photo_paths?.length || 0);

// Primer motivo por el que el ítem no se puede publicar (texto para el vendedor) o null.
export function itemError(d, game0) {
  const game = d.game || game0;
  if (!d.selected) return "elige una carta o producto";
  const sealed = isSealedSel(d.selected);
  if (game === "yugioh" && !sealed && (!d.form.yugioh_rarity || !d.form.yugioh_set_code)) return "elige la rareza + set exactos de tu Yu-Gi-Oh";
  if (photoCount(d) < MIN_PHOTOS) return `mínimo ${MIN_PHOTOS} fotos`;
  if (!(parseFloat(d.form.price) >= 0.25)) return "el precio mínimo es $0.25";
  if (d.form.graded && !(d.form.graded_grade || "").trim()) return "indica la calificación (ej. 10, 9.5)";
  if (parseInt(d.form.quantity, 10) > 1 && !d.form.bulk_disclaimer_accepted) return "acepta que TODAS las unidades están en la misma condición";
  return null;
}
export const isComplete = (d, game) => itemError(d, game) === null;

// Forma persistible de la cola (sin File; las fotos ya subidas viajan como rutas).
export const serializeQueue = (drafts) => drafts.filter((d) => d.selected).map(({ id, selected, form, photo_paths, error, game }) => ({
  id, form, photo_paths: photo_paths || [], error: error || null, game: game || null,
  selected: {
    id: selected.id, name: selected.name, image_url: selected.image_url, set_name: selected.set_name, local_id: selected.local_id,
    rarity: selected.rarity, game: selected.game, is_sealed: selected.is_sealed, product_type: selected.product_type,
    variants_available: selected.variants_available, holo_card: selected.holo_card,
    first_edition_available: selected.first_edition_available, all_printings: selected.all_printings,
  },
}));

const publicationForm = (d, game, paths) => {
  const f = d.form;
  const fd = new FormData();
  const put = (k, v) => fd.append(k, v);
  put("card_id", d.selected.id); put("game", game); put("language", f.language); put("condition", f.condition);
  put("variant", f.variant); put("price", f.price); put("quantity", f.quantity); put("ship_cost", f.ship_cost);
  if (f.free_ship_min_items) put("free_ship_min_items", f.free_ship_min_items);
  if (f.free_ship_min_amount) put("free_ship_min_amount", f.free_ship_min_amount);
  if (f.graded) { put("graded", "true"); put("graded_house", f.graded_house || "PSA"); put("graded_grade", f.graded_grade || ""); }
  if (f.language === "otro") put("language_other", f.language_other);
  put("photo_paths", [...(d.photo_paths || []), ...paths].join(","));
  put("client_ref", d.id);
  if (f.first_edition) put("first_edition", "true");
  if (typeof f.local_only === "boolean") put("local_only", String(f.local_only));
  if (parseInt(f.quantity, 10) > 1) put("bulk_disclaimer_accepted", "true");
  if (game === "yugioh") {
    if (f.yugioh_rarity) put("yugioh_rarity", f.yugioh_rarity);
    if (f.yugioh_set_code) put("yugioh_set_code", f.yugioh_set_code);
    if (f.yugioh_rarity_note) put("yugioh_rarity_note", f.yugioh_rarity_note);
  }
  return fd;
};

// Publica un ítem (sube las fotos pendientes y crea la publicación). Idempotente por client_ref = d.id.
export async function publishItem(d, game0, onSub = () => {}) {
  const game = d.game || game0;
  let n = 0;
  const paths = await Promise.all(d.photos.map(async (f, pi) => {
    try { const p = await uploadPublic(f); n += 1; onSub(`${n}/${d.photos.length} fotos subidas`); return p; }
    catch (err) { throw new Error(`Foto ${pi + 1}: ${humanUploadError(err)}`); }
  }));
  onSub("Creando publicación…");
  const r = await api.post("/publications", publicationForm(d, game, paths), { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
  return r.data;
}

export const errorText = (err) => {
  const isAxios = err && (err.response || err.request || err.isAxiosError);
  return (!isAxios && err instanceof Error && err.message) ? err.message : humanUploadError(err);
};

// Publica en lote: las que fallan no frenan a las demás. Devuelve un resultado por ítem.
export async function publishBatch(list, game, onProgress = () => {}) {
  const out = [];
  for (let i = 0; i < list.length; i++) {
    const d = list[i];
    const name = d.selected?.name || `Carta ${i + 1}`;
    onProgress({ step: `Publicando ${i + 1} de ${list.length}`, current: i, total: list.length, sub: `"${name}"` });
    try {
      const pub = await publishItem(d, game, (sub) => onProgress({ step: `Publicando ${i + 1} de ${list.length}`, current: i, total: list.length, sub: `"${name}" · ${sub}` }));
      out.push({ id: d.id, name, ok: true, status: pub.status });
    } catch (err) {
      out.push({ id: d.id, name, ok: false, error: errorText(err) });
    }
  }
  return out;
}

export function summarize(results) {
  const active = results.filter((r) => r.ok && r.status === "active").length;
  const review = results.filter((r) => r.ok && r.status === "pending_review").length;
  const failed = results.filter((r) => !r.ok);
  const ok = active + review;
  const text = failed.length
    ? `${ok} ${ok === 1 ? "publicada" : "publicadas"} · ${failed.length} no se ${failed.length === 1 ? "pudo" : "pudieron"} publicar`
    : `${active} publicada(s), ${review} en revisión`;
  return { active, review, failed, ok, text };
}
